import { Comms } from '@leverege/comms'
import { jwtDecode } from 'jwt-decode'

import {
  DEVICE_CODE_GRANT_TYPE,
  DEVICE_CODE_SUCCESS_CODE_TYPE,
  JSON_PRE,
  JWT_TOKEN_TYPE,
  PAYLOAD_TYPE_2FA_REQUIRE_ENROLL,
  PAYLOAD_TYPE_2FA_VERIFY,
  PROFILE_TYPE_API_ACCESS
} from './Constants.js'
import Routes from './routes/index.js'
import TemporaryUser from './routes/temporaryUser/TemporaryUser.js'
import TemporaryUsers from './routes/temporaryUser/TemporaryUsers.js'
import { encodeQ, pick } from './Utils.js'

function authToken( token, type = JWT_TOKEN_TYPE ) {
  return { getToken( ) { return type == null ? token : `${type} ${token}` } }
}

export default class Api extends Comms {

  constructor( opts ) {
    super( opts )
    this._users = Routes.users( this )
    this._projects = Routes.collection( this, 'v1/project', false, true )
    this._maintenanceEvents = Routes.collection( this, 'v1/maintenanceEvent', true, true )
    this.tokenKey = opts.tokenKey || 'id_token'
    this.rememberMeTokenKey = opts.rememberMeTokenKey || 'remember_me_token'
    this.refreshTokenKey = opts.refreshTokenKey || 'refresh_token'

    if ( opts.storeToken && localStorage ) {
      const tkn = localStorage.getItem( this.tokenKey )
      const refreshTkn = localStorage.getItem( this.refreshTokenKey )
      if ( refreshTkn ) {
        this.setRefreshToken( { refreshToken : refreshTkn } )
      } else if ( tkn ) {
        this.options.auth = authToken( tkn )
      }
    }
  }

  static Create( opts ) {
    return new Api( opts );
  }
  
  encodeQueryParams( options ) {
    const hc = options && options.hookContext
    if ( hc != null && typeof hc === 'object' ) {
      try {
        const json = JSON.stringify( hc )
        const opts = { ...options }
        if ( typeof btoa === 'function' ) {
          opts.hookContext = JSON_PRE + btoa( json )
        } else if ( typeof Buffer === 'function' ) {
          opts.hookContext = JSON_PRE + Buffer.from( json ).toString( 'base64' )
        } else {
          return super.encodeQueryParams( options )
        }
        return super.encodeQueryParams( opts )
      } catch ( err ) { 
        // eslint-disable-next-line no-console
        console.log( 'error stringifying hookContext' )
      }
    }
    return super.encodeQueryParams( options )
  }
  
  // ----------- Registration 

  login( username, password, projectId, options ) {
    this.logout()
    const pid = projectId === null ? null : projectId || this.options.projectId
    const body = { username, projectId : pid }

    if ( options?.userType === 'temporary' ) {
      body.accessCode = password
    } else {
      body.password = password
    }

    if ( options?.userCode ) {
      body.user_code = options.userCode
      delete options.userCode
    }

    return this.post( `v1/login${encodeQ( options )}`, body )
      .then( ( r ) => {
        this.projectId = projectId

        return this.transformLoginResponseAndSetToken( r )
      } )
      .catch( ( err ) => {
        if ( this.options.storeToken && localStorage ) {
          localStorage.removeItem( this.rememberMeTokenKey )
        }

        throw err
      } )
  }

  loginWith2FA( code, method, rememberMe, projectId, options ) {
    const pid = projectId === null ? null : projectId || this.options.projectId
    const body = { code, method, rememberMe, projectId : pid }

    if ( options?.userCode ) {
      body.user_code = options.userCode
      delete options.userCode
    }
    return this.post( `v1/loginWith2FA${encodeQ( options )}`, body )
      .then( ( r ) => {
        this.projectId = projectId

        return this.transformLoginResponseAndSetToken( r )
      } )
  }

  requestLoginCode( username, method, emailTemplate, projectId, options ) {
    const pid = projectId === null ? null : projectId || this.options.projectId
    return this.post( `v1/login${encodeQ( options )}`, { username, requestLoginCodeVia : method, emailTemplate, projectId : pid } )
  }

  requestDeviceCode( clientId, projectId ) {
    const pid = projectId === null ? null : projectId || this.options.projectId

    return this.post( 'v1/login/device/code', {
      client_id : clientId,
      grant_type : DEVICE_CODE_GRANT_TYPE,
      projectId : pid
    } )
  }

  confirmUserCode( userCode, clientId, projectId ) {
    const pid = projectId === null ? null : projectId || this.options.projectId

    return this.post( 'v1/login/device/code/confirm', {
      client_id : clientId,
      grant_type : DEVICE_CODE_GRANT_TYPE,
      projectId : pid,
      user_code : userCode
    } )
  }

  requestDeviceAccessToken( deviceCode, clientId, projectId ) {
    const pid = projectId === null ? null : projectId || this.options.projectId

    return this.post( 'v1/login/device/accessToken', {
      client_id : clientId,
      device_code : deviceCode,
      grant_type : DEVICE_CODE_GRANT_TYPE,
      projectId : pid
    } )
      .then( ( r ) => {
        this.projectId = pid
        
        return this.transformLoginResponseAndSetToken( r )
      } )
  }

  loginWithCode( username, loginCode, projectId, options ) {
    this.logout()
    const pid = projectId === null ? null : projectId || this.options.projectId

    const body = { username, loginCode, projectId : pid }

    if ( options?.userCode ) {
      body.user_code = options.userCode
      delete options.userCode
    }

    return this.post( `v1/login${encodeQ( options )}`, body )
      .then( ( r ) => {
        this.projectId = projectId

        return this.transformLoginResponseAndSetToken( r )
      } )
      .catch( ( err ) => {
        if ( this.options.storeToken && localStorage ) {
          localStorage.removeItem( this.rememberMeTokenKey )
        }
        
        throw err
      } )
  }

  /**
   * 
   * @param {object} clientOpts contains the id of oidc client and the projectId
   * @param {string} clientOpts.projectId the projectId to log into
   * @param {string} clientOpts.id contains the id of oidc client
   * @param {string} clientOpts.alias contains the alias of oidc client
   * @param {string} success the url of the successful login. A query param token will
   * be appended to this url, which is the resulting jwt token. Call Api.setToken() with it.
   * @param {string} failure the url of the failure result
   * @param {object} opts contains info about handling of url
   * @param {string} opts.prompt the prompt form to use (none, login, consent, select_account)
   * @param {string} opts.display the display form to use (page,popup,touch,wrap)
   */
  oidcLoginUrl( clientOpts, success, failure, opts ) {
    const q = pick( [ 'alias', 'projectId' ], clientOpts, { success, failure } )
    pick( [ 'display', 'prompt' ], clientOpts )
    if ( clientOpts.id ) {
      q.clientId = clientOpts.id // remap id to clientId
    }
    const url = `${this.options.host}/v1/oidc/login${encodeQ( q )}`
    return url
  }

  getToken( method, url, extraParams ) {
    let tkn = super.getToken( method, url, extraParams )

    if ( !tkn ) {
      tkn = this.getRememberMeToken( method, url, extraParams )
    }

    return tkn
  }

  getRememberMeToken( method, url, extraParams ) {
    if ( method === 'POST' && url === `${this.host}/v1/login` ) {
      if ( this.options.storeToken && localStorage ) {
        const tkn = localStorage.getItem( this.rememberMeTokenKey ) || null

        if ( tkn ) {
          return `${JWT_TOKEN_TYPE} ${tkn}`
        }
      }
    }

    return null
  }

  transformLoginResponseAndSetToken( response ) {
    if ( response.type === PROFILE_TYPE_API_ACCESS || response.code === DEVICE_CODE_SUCCESS_CODE_TYPE ) {
      return response
    }

    if ( response.idToken ) {
      response.profile.token = response.idToken
    }

    if ( response.refreshToken ) {
      response.profile.refreshToken = response.refreshToken
      response.profile.accessToken = response.accessToken
      this.setRefreshToken( response )
    } else if ( response.accessToken ) {
      response.profile.accessToken = response.accessToken
      this.setAccessToken( response )
    } else {
      this.setToken( response.idToken )
    }
    
    if ( response.rememberMeToken ) {
      this.setRememberMeToken( response.rememberMeToken )
    }

    return { ...response.profile, message : response.message }
  }

  setToken( jwt, type = JWT_TOKEN_TYPE ) {
    super.setToken( jwt, type )
    const payload = jwtDecode( jwt )

    if ( this.options.storeToken && 
          localStorage && 
          payload.type !== PAYLOAD_TYPE_2FA_VERIFY && 
          payload.type !== PAYLOAD_TYPE_2FA_REQUIRE_ENROLL ) {
      localStorage.setItem( this.tokenKey, jwt )
    }
  }

  setRefreshToken( r, type = JWT_TOKEN_TYPE ) {
    const token = r.accessToken ? `${type} ${r.accessToken}` : null
    const refreshToken = `${type} ${r.refreshToken}`
    this.setAuth( {
      getToken : () => {
        // TODO: check expiration before making request
        return token
      },
      getRefreshToken : () => {
        return refreshToken
      },
      refreshToken : () => {
        return this.verify( { extend : false } )
      }
    } )
    if ( this.options.storeToken ) {
      if ( localStorage ) {
        localStorage.setItem( this.refreshTokenKey, r.refreshToken )
      }
    }
  }

  setAccessToken( r, type = JWT_TOKEN_TYPE ) {
    const token = `${type} ${r.accessToken}`
    const refreshToken = this.options.auth.getRefreshToken()
    this.setAuth( {
      getToken : () => {
        // TODO: check expiration before making request
        return token
      },
      getRefreshToken : () => {
        return refreshToken
      },
      refreshToken : () => {
        return this.verify( { extend : false } )
      }
    } )
  }

  setRememberMeToken( jwt ) {
    if ( this.options.storeToken && localStorage && jwt ) {
      localStorage.setItem( this.rememberMeTokenKey, jwt )
    }
  }
  
  /**
   * Verifies that the current auth token is valid. If the
   * auth is a JWT token representing a user, the Promise retreives a 
   * profile and a new token. If the auth represents an Api Access
   * key, the Api Access information is returned
   **/
  verify( opts ) {
    if ( ( this.options.auth == null || 
         this.options.auth.getToken == null ||
         this.options.auth.getToken( 'GET', `${this.options.host}/v1/verify` ) == null ) &&
         !this.canRefreshToken( 'GET', `${this.options.host}/v1/verify` )
    ) {
      return new Promise( ( resolve, reject ) => {
        reject( { status : 403, message : 'no token' } )
      } )
    }
    const nOpts = { ...opts, extend : opts?.extend == null ? true : opts.extend }
    return this.get( `v1/verify${encodeQ( nOpts )}`, { useRefresh : true } )
      .then( r => this.transformLoginResponseAndSetToken( r ) )
      .catch( ( x ) => {
        this.options.auth = null
        if ( this.options.storeToken && localStorage && !x.twoFactor ) {
          localStorage.removeItem( this.tokenKey )
          localStorage.removeItem( this.refreshTokenKey )
        }
        throw x
      } )
  }

  logout( ) {
    if ( this.options.storeToken && localStorage ) {
      localStorage.removeItem( this.tokenKey )
      localStorage.removeItem( this.refreshTokenKey )
    }
    this.options.auth = null
    return Promise.resolve( true )
  }

  register( ) {
    return this.get( 'v1/register' )
  }

  // ----------- Access

  users() { 
    return this._users
  }

  user( user ) { 
    return Routes.user( this, user )
  }

  teams() { 
    return this.projects()
  }

  team( project ) {
    const prj = project === undefined ? this.options.projectId : project
    return Routes.project( this, prj )
  }

  maintenanceEvent( eventId ) {
    return {
      get : () => this._maintenanceEvents.get( eventId ),
      delete : () => this._maintenanceEvents.delete( eventId ),
      update : data => this._maintenanceEvents.updateRaw( eventId, data )
    }
  }

  maintenanceEvents() {
    return this._maintenanceEvents
  }

  projects() { 
    return this._projects
  }

  project( project ) {
    const prj = project === undefined ? this.options.projectId : project
    return Routes.project( this, prj )
  }

  blueprint( blueprint ) {
    return Routes.blueprint( this, blueprint )
  }

  attribute( attribute ) {
    return Routes.attribute( this, attribute )
  }

  interface( system, type ) {
    return Routes.interface( this, system, type )
  }

  system( system ) { 
    return Routes.system( this, system )
  }

  messageRoute( route ) { 
    return Routes.messageRoute( this, route )
  }

  oidcClient( route ) { 
    return Routes.oidcClient( this, route )
  }

  /**
   * Returns a device access object. 
   * @param dev either the deviceId string, or an object containing
   * { id : <deviceId string>, systemId: <systemId string> }
   **/ 
  device( dev ) {
    return Routes.device( this, dev )
  }

  scenario( scen ) {
    return Routes.scenario( this, scen )
  }

  script( script ) {
    return Routes.script( this, script )
  }

  template( template ) {
    return Routes.template( this, template )
  }

  apiAccess( access ) {
    return Routes.apiAccess( this, access )
  }

  timer( timer ) {
    return Routes.timer( this, timer )
  }

  // systemContact( contact ) {
  //   return Routes.apiAccess( this, contact )
  // }

  // contact( )

  activateAccount( data ) {
    return this.post( 'v1/user/activateAccount', data )
  }

  resendActivation( username, projectId, emailOptions, opts ) {
    const data = projectId == null ? { username } : { username, projectId }
    if ( emailOptions ) {
      data.emailOptions = emailOptions
    }
    return this.post( 'v1/user/resendActivation', data, opts )
  }

  forgotPassword( username, projectId, options ) {
    projectId = projectId === null ? null : projectId || this.options.projectId
    return this.post( 'v1/user/forgotPassword', { username, projectId, ...options } )
  }

  resetPassword( data ) {
    return this.post( 'v1/user/resetPassword', data )
  }

  resetPasswordWithCode( data ) {
    return this.post( 'v1/user/resetPasswordWithCode', data )
  }
  
  refreshTokens() {
    return {
      list : options => this.child( 'v1/refreshToken' ).get( options ),
    }
  }

  refreshToken( rt ) {
    const id = getId( rt )
    return {
      get : options => this.child( `v1/refreshToken/${id}` ).get( options ),
      revoke : ( data, options ) => this.child( `v1/refreshToken/${id}/revoke` ).patch( data, options ),
    }
  }

  temporaryUsers() {
    return new TemporaryUsers( this )
  }

  temporaryUser() {
    return new TemporaryUser( this )
  }
  // ----------- Team 
  /*
  getTeams( ) {
    return this.get( 'v1/project' )
  }

  createTeam( name, options ) {
    return this.post( `v1/project${encodeQ( options )}`, { name } )
  }

  deleteTeam( project, options ) {
    project = getId( project )
    return this.delete( `v1/project/${project}${encodeQ( options )}` )
  }

  getTeam( projectId, options ) {
    if ( options == null ) {
      options = { children : [ 'blueprint' ] } // backward compat
    }
    return this.get( `v1/project/${projectId}${encodeQ( options )}` )
  }

  updateTeam( project, patchOps ) {
    project = getId( project )
    patchOps = getPatchOpsArray( patchOps )
    return this.patch( `v1/project/${project}/`, patchOps )
  }
  addApiAccess( project, apiAccess ) {
    project = getId( project )
    return this.post( `v1/project/${project}/apiAccess`, apiAccess )
  }

  updateApiAccess( apiAccess, patchOps ) {
    patchOps = getPatchOpsArray( patchOps )
    return this.patch( `v1/project/${apiAccess.projectId}/apiAccess/${apiAccess.id}`, patchOps )
  }

  removeApiAccess( apiAccess ) {
    return this.delete( `v1/project/${apiAccess.projectId}/apiAccess/${apiAccess.id}` )
  }

  getBlueprints( projectId, options ) {
    return this.get( `v1/project/${projectId}/blueprint${encodeQ( options )}` )
  }
  // ----------- Blueprint 
  addBlueprint( project, blueprint ) {
    const id = typeof project === 'string' ? project : project.id
    return this.post( `v1/project/${id}/blueprint`, blueprint );
  }

  getBlueprint( projectId, blueprintId, options ) {
    // backward compat
    if ( options == null ) {
      options = {
        children : 'attribute'
      }
    }
    return this.get( `v1/project/${projectId}/blueprint/${blueprintId}${encodeQ( options )}` )
  }

  updateBlueprint( blueprint, patchOps ) {
    patchOps = getPatchOpsArray( patchOps )
    return this.patch( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}`, patchOps )
  }

  removeBlueprint( blueprint ) {
    return this.delete( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}` )
  }

  simulateBlueprint( blueprint ) {
    return this.post( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/simulate` );
  }
*/
  // ----------- Attributes 
  /*
  addAttribute( blueprint, attribute ) {
    return this.post( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/attribute`, attribute );
  }
  
  updateAttribute( blueprint, attribute, patchOps ) {
    patchOps = getPatchOpsArray( patchOps )
    return this.patch( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/attribute/${attribute.id}`, patchOps );
  }

  removeAttribute( blueprint, attribute ) {
    return this.delete( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/attribute/${attribute.id}`, { } );
  }

  removeAttributes( blueprint, attributeIds ) {
    return this.delete( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/attributes`, attributeIds );
  }*/

  // ----------- Alert 

  /*
  getAlert( blueprint, alert ) {
    alert = getId( alert )
    return this.get( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/alert/${alert}` );
  }

  addAlert( blueprint, alert ) {
    return this.post( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/alert`, alert );
  }
  
  updateAlert( blueprint, alert, patchOps ) {
    patchOps = getPatchOpsArray( patchOps )
    alert = getId( alert )
    return this.patch( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/alert/${alert}`, patchOps );
  }

  removeAlert( blueprint, alert ) {
    alert = getId( alert )
    return this.delete( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/alert/${alert}`, { } );
  }

  removeAlerts( blueprint, alertIds ) {
    return this.delete( `v1/project/${blueprint.projectId}/blueprint/${blueprint.id}/alerts`, alertIds );
  }
  */

  // ----------- Contact 
  /*
  getContacts( system, options ) {
    system = getId( system )
    return this.get( `v1/system/${system}/contact/${encodeQ( options )}` );
  }

  getContact( system, contact, options ) {
    system = getId( system )
    contact = getId( contact )
    return this.get( `v1/system/${system}/contact/${contact}${encodeQ( options )}` );
  }

  addContact( system, contact ) {
    system = getId( system )
    return this.post( `v1/system/${system}/contact`, contact );
  }
  
  updateContact( system, contact, patchOps ) {
    system = getId( system )
    contact = getId( contact )
    patchOps = getPatchOpsArray( patchOps )
    return this.patch( `v1/system/${system}/contact/${contact}`, patchOps );
  }

  removeContact( system, contact ) {    
    system = getId( system )
    contact = getId( contact )
    return this.delete( `v1/system/${system}/contact/${contact}`, { } );
  }

  // ----------- Scenario
  addScenario( projectId, scenario ) {
    return this.post( `v1/project/${projectId}/scenario`, scenario );
  }

  getScenarios( projectId, blueprintId, options ) {
    if ( blueprintId ) {
      return this.get( `v1/project/${projectId}/blueprint/${blueprintId}/scenario${encodeQ( options )}` )
    }
    return this.get( `v1/project/${projectId}/scenario${encodeQ( options )}` )
  }

  getScenario( projectId, scenarioId, options ) {
    return this.get( `v1/project/${projectId}/scenario/${scenarioId}${encodeQ( options )}` )
  }
  */

  /*
  putScenario( scenario ) {
    return this.put( `v1/project/${scenario.projectId}/scenario/${scenario.id}`, scenario )
  }

  // updateScenario( system, patchOps ) {
  //   return this.patch( `v1/system/${system.id}/`, patchOps )
  // }

  removeScenario( projectId, scenarioId ) {
    return this.delete( `v1/project/${projectId}/scenario/${scenarioId}` )
  }
  playScenario( systemId, deviceId, scenarioId, options ) {
    options = options || {}
    options.scenarioId = scenarioId
    return this.post( `v1/system/${systemId}/device/${deviceId}/scenario`, options )
  }
  stopScenario( systemId, deviceId ) {
    return this.delete( `v1/system/${systemId}/device/${deviceId}/scenario` )
  }
  */
  // ----------- Systems
  /*
  addSystem( projectId, system ) {
    return this.post( `v1/project/${projectId}/system`, system );
  }

  getSystems( projectId, options ) {
    return this.get( `v1/project/${projectId}/system${encodeQ( options )}` )
  }

  getSystem( systemId, options ) {
    // options.children = options.children || 'device'
    //    return this.get( `v1/system/${systemId}?children=device` )
    return this.get( `v1/system/${systemId}${encodeQ( options )}` )

  }

  updateSystem( system, patchOps ) {
    system = getId( system )
    patchOps = getPatchOpsArray( patchOps )

    return this.patch( `v1/system/${system}/`, patchOps )
  }

  removeSystem( system ) {
    return this.delete( `v1/project/${system.projectId}/system/${system.id}` )
  }
*/
  // restartSystemTransponders( system, options ) {
  //   let id = getId( system )
  //   return this.put( `v1/system/${id}/restartTransponders${encodeQ(options)}`, null )
  // }
  /*
  requestDataToken( systemId ) {
    return this.get( `v1/${systemId}/dataToken` )
  }
  */

  // ----------- Device
  /*
  addDevice( systemId, device ) {
    return this.post( `v1/system/${systemId}/device`, device );
  }

  getDevices( systemId, options ) {
    return this.get( `v1/system/${systemId}/device${encodeQ( options )}` )
  }

  getDevice( systemId, deviceId, options ) {
    return this.device( { id : deviceId, systemId } ).get( options )
    //    return this.get( `v1/system/${systemId}/device/${deviceId}${encodeQ(options)}` )
  }
*/
  /**
   *  Get the device by networkId, key and value
   **/
  getDeviceByNetworkAlias( networkId, key, value, options ) {
    networkId = getId( networkId )
    return this.get( `v1/network/${networkId}/device/${key}/${encodeURIComponent( value )}${encodeQ( options )}` )
  }

  /**
   * Sets the name of of the device on the specified network
   **/
  setNetworkAlias( device, networkId, key, value, options ) {
    return this.device( device ).setNetworkAlias( networkId, key, value, options )
    // const add = [ { op:"replace", path : 'networkAliases/' + networkId + '/' + key, value } ]
    // return this.updateDevice( device, add )
  }

  /**
   * @deprecated Use getDeviceByNetworkAlias
   * Get the device by alias. There are two variant of this:
   *    getDeviceByAlias( 'iothub', 'device-2' ) - looks up the iothub alias
   *    getDeviceByAlias( 'value' ) - uses 'uuid' as the alias key
   **/
  getDeviceByAlias( aliasKey, aliasValue, options ) {
    const isValue = aliasValue != null && typeof aliasValue !== 'object'
    if ( arguments.length >= 2 && isValue ) {
      return this.get( `v1/deviceByAlias/${aliasKey}/${encodeURIComponent( aliasValue )}${encodeQ( options )}` )
    }
    return this.get( `v1/deviceByAlias/uuid/${encodeURIComponent( aliasKey )}${encodeQ( options )}` )
  }
  /*
  updateDevice( device, patchOps, options ) {
    return this.device( device ).update( patchOps, options )
    // patchOps = getPatchOpsArray( patchOps )
    // return this.patch( `v1/system/${device.systemId}/device/${device.id}`, patchOps )
  }

  messageDevice( device, message, options ) {
    return this.device( device ).message( message, options )
    //    return this.post( `v1/system/${device.systemId}/device/${device.id}/sendMessage${encodeQ(options)}`, message )
  }

  startDevice( device, options ) {
    return this.device( device ).startSim( options )
    //    return this.put( `v1/system/${device.systemId}/device/${device.id}/start${encodeQ(options)}`, data )
  }

  pauseDevice( device, options ) {
    return this.device( device ).pauseSim( options )
    // return this.put( `v1/system/${device.systemId}/device/${device.id}/pause${encodeQ(options)}`, null )
  }

  resetDevice( device, options ) {
    return this.device( device ).resetSim( options )
    // return this.put( `v1/system/${device.systemId}/device/${device.id}/reset${encodeQ(options)}`, null )
  }

  // restartDeviceTransponder( device, options ) {
  //   return this.put( `v1/system/${device.systemId}/device/${device.id}/restartTransponder${encodeQ(options)}`, null )
  // }

  deviceLog( device ) {
    return this.get( `v1/system/${device.systemId}/device/${device.id}/log`, null )
  }

  removeDevice( device ) {
    return this.device( device ).delete()
    //    return this.delete( `v1/system/${device.systemId}/device/${device.id}` )
  }

  setValue( device, path, value ) {
    return this.device( device ).setValue( path, value, null )

    // let body = path
    // if ( typeof path == 'string' ) {
    //   body = { values: [ 
    //       {path : path, value : value}
    //     ]}
    // }
    // return this.put( `v1/system/${device.systemId}/device/${device.id}/setValue`, body )
  }

  setDeviceUpdater( device, path, updater, options ) {
    return this.device( device ).setUpdater( path, updater, options )
    // return this.put( `v1/system/${device.systemId}/device/${device.id}/updater`, { path : path, updater : updater } )
  }

  getDeviceUpdater( device, path, options ) {
    return this.device( device ).getUpdater( path, options )

    // options = { ...options, path : path }
    // return this.get( `v1/system/${device.systemId}/device/${device.id}/updater${encodeQ(options)}` )
  }

  getDeviceUpdaters( device, options ) {
    return this.device( device ).getUpdaters( options )
    //    return this.get( `v1/system/${device.systemId}/device/${device.id}/updater${encodeQ(options)}` )
  }

  // options = { start : <string parsable by new Date()), end : <option date string>}
  getHistorical( device, options ) {
    return this.device( device ).getHistorical( options )
    //    return this.get( `v1/system/${device.systemId}/device/${device.id}/historical${encodeQ(options)}` )
  }
*/

  // network devices
  getSharedNetworks( ) {
    return this.get( 'v1/sharedNetwork' )
  }

  getSharedNetwork( networkId, options ) {
    return this.get( `v1/sharedNetwork/${networkId}${encodeQ( options )}` )
  }

/*
  getNetworks( project, options ) {
    project = getId( project )
    return this.get( `v1/project/${project}/network${encodeQ( options )}` )
  }

  getNetwork( project, networkId, options ) {
    project = getId( project )
    return this.get( `v1/project/${project}/network/${networkId}${encodeQ( options )}` )
  }

  addNetwork( project, network ) {
    project = getId( project )
    return this.post( `v1/project/${project}/network`, network );
  }

  updateNetwork( network, patchOps ) {
    patchOps = getPatchOpsArray( patchOps )
    return this.patch( `v1/project/${network.projectId}/network/${network.id}`, patchOps )
  }

  removeNetwork( network ) {
    return this.delete( `v1/project/${network.projectId}/network/${network.id}` )
  }
*/
  // ----------- Generic calls 
}

function getId( objOrId ) {
  if ( typeof objOrId === 'object' ) {
    return objOrId.id
  }
  return objOrId
}
